import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, ShoppingCart, Menu, X, User, LogOut } from 'lucide-react';
import SmartSearch from './SmartSearch';

const Navbar = () => {
  const [cartCount, setCartCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadUser = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) setCartCount(JSON.parse(savedCart).length);
      const savedUser = JSON.parse(localStorage.getItem('user') || 'null');
      setUser(savedUser);
    };
    loadUser();
    window.addEventListener('cartUpdated', loadUser);
    return () => window.removeEventListener('cartUpdated', loadUser);
  }, []);

  // Close dropdowns on location changes
  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Handle clicking outside user dropdown to close it
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownOpen && !e.target.closest('.user-dropdown-container')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [dropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`navbar navbar-expand-lg sticky-top ${scrolled ? 'scrolled' : ''}`}
      style={{
        backgroundColor: '#000000',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        padding: scrolled ? '10px 0' : '18px 0',
      }}
    >
      <div className="container">
        {/* Brand */}
        <Link className="navbar-brand d-flex align-items-center gap-2" to="/" style={{ textDecoration: 'none' }}>
          <span style={{ color: '#D4AF37', fontSize: '1.6rem' }}>✦</span>
          <div>
            <span style={{
              fontFamily: 'Playfair Display, serif',
              fontWeight: 700,
              fontSize: '1.3rem',
              color: '#ffffff',
              display: 'block',
              lineHeight: 1.2,
              letterSpacing: '1px'
            }}>
              SYRA
            </span>
            <span style={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '0.65rem',
              color: '#D4AF37',
              letterSpacing: '3px',
              textTransform: 'uppercase'
            }}>
              Food Point & Grill
            </span>
          </div>
        </Link>

        {/* Mobile Toggle */}
        <button
          className="navbar-toggler border-0 d-lg-none"
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
          style={{ color: '#ffffff', padding: '8px' }}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Nav */}
        <div className={`collapse navbar-collapse ${mobileOpen ? 'show' : ''}`} id="navbarNav"
          style={{
            ...(mobileOpen ? {
              display: 'block',
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'rgba(26, 26, 26, 0.98)',
              backdropFilter: 'blur(20px)',
              padding: '20px',
              borderRadius: '0 0 12px 12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            } : {})
          }}
        >
          <ul className="navbar-nav ms-auto align-items-center gap-1">
            <li className="nav-item me-lg-3 mb-2 mb-lg-0">
              <SmartSearch />
            </li>
            {[
              { path: '/', label: 'Home' },
              { path: '/menu', label: 'Menu' },
              { path: '/gallery', label: 'Gallery' },
              { path: '/contact', label: 'Contact' },
            ].map(item => (
              <li className="nav-item" key={item.path}>
                <Link
                  className="nav-link"
                  to={item.path}
                  style={{
                    color: isActive(item.path) ? '#D4AF37' : 'rgba(255,255,255,0.85)',
                    fontWeight: isActive(item.path) ? 600 : 500,
                  }}
                >
                  {item.label}
                </Link>
              </li>
            ))}

            {/* Phone */}
            <li className="nav-item d-none d-xl-flex align-items-center ms-3" style={{ whiteSpace: 'nowrap' }}>
              <Phone size={14} style={{ color: '#D4AF37', marginRight: '6px' }} />
              <a href="tel:+256772206129" style={{ color: '#D4AF37', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                +256&nbsp;772&nbsp;206129
              </a>
            </li>

            {/* Cart */}
            <li className="nav-item ms-2">
              <Link className="nav-link position-relative d-flex align-items-center" to="/cart" style={{ color: 'rgba(255,255,255,0.85)' }}>
                <ShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                    style={{ backgroundColor: '#D4AF37', fontSize: '0.65rem', minWidth: '18px', padding: '3px 6px' }}>
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>

            {/* Auth */}
            {user ? (
              <li className="nav-item ms-3 dropdown user-dropdown-container">
                <button className="btn nav-link d-flex align-items-center gap-2" style={{ color: '#fff', border: 'none', background: 'none', padding: '8px 12px' }}
                  onClick={() => setDropdownOpen(!dropdownOpen)}>
                  <User size={16} color="#D4AF37" />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {user.role === 'admin' ? 'Account' : user.name?.split(' ')[0] || 'Account'}
                  </span>
                </button>
                <div className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`} style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px 8px', minWidth: '200px' }}>
                  <div style={{ padding: '4px 14px 10px', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '8px' }}>
                    Signed in as:<br/>
                    <strong style={{ color: '#D4AF37', fontSize: '0.78rem' }}>{user.name}</strong>
                  </div>
                  <Link to="/my-orders" className="dropdown-item" onClick={() => setDropdownOpen(false)}
                    style={{ color: '#fff', borderRadius: '6px', padding: '8px 14px', fontSize: '0.85rem' }}>
                    My Orders
                  </Link>
                  <Link to="/profile" className="dropdown-item" onClick={() => setDropdownOpen(false)}
                    style={{ color: '#fff', borderRadius: '6px', padding: '8px 14px', fontSize: '0.85rem' }}>
                    Profile Settings
                  </Link>
                  <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.06)', margin: '8px 0' }}></div>
                  <button className="dropdown-item" onClick={() => { handleLogout(); setDropdownOpen(false); }}
                    style={{ color: '#e74c3c', borderRadius: '6px', padding: '8px 14px', fontSize: '0.85rem', background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              </li>
            ) : (
              <li className="nav-item ms-3">
                <Link className="btn btn-outline-light btn-sm" to="/login" style={{ borderRadius: '4px', fontSize: '0.8rem', letterSpacing: '1px', padding: '8px 20px' }}>
                  Login
                </Link>
              </li>
            )}

            {/* Reserve CTA */}
            <li className="nav-item ms-3">
              <Link
                className="btn btn-gold"
                to="/booking"
                style={{
                  padding: '10px 28px',
                  fontSize: '0.8rem',
                  letterSpacing: '1.5px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                }}
              >
                Reserve Table
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
